// Route guard for governance pages. Shared by the router and by the next
// shell's page overrides (src/next/shell/NextPageOutlet.tsx), so an override
// renders behind exactly the guard the router declares (PLAN-20260922,
// Agion#2179).
import React from 'react'
import { Navigate } from 'react-router'
import { useAuth, hasGovernancePermission } from '@/shared/auth/unified-auth'
import { NavigationGate, type NavigationPage } from '@/shared/auth/navigation-gate'

export const GovernanceRouteGuard: React.FC<{
  children: React.ReactNode;
  requiredPermission?: 'read' | 'write' | 'admin';
  /** The sidebar entry's navigation permission, as in ProtectedRoute (agion/Agion#2556). */
  requiredNavigation?: NavigationPage;
}> = ({ children, requiredPermission = 'read', requiredNavigation }) => {
  const { isAuthenticated, user } = useAuth();

  // Check if we're in demo/mock mode (environment variable controlled)
  // Note: VITE_DISABLE_AUTH removed - use VITE_USE_MOCK_DATA for development
  const isDemo = import.meta.env['VITE_USE_MOCK_DATA'] === 'true' ||
                 import.meta.env['VITE_MOCK_AUTH'] === 'true' ||
                 import.meta.env['VITE_AUTH_DISABLED'] === 'true' ||
                 false;

  // In demo mode, bypass authentication check but still enforce permissions
  if (isDemo) {
    // Still check permissions if user is logged in
    if (isAuthenticated && user && !hasGovernancePermission(user.role, requiredPermission)) {
      return <Navigate to="/unauthorized" replace />;
    }
    // Allow access in demo mode even without authentication
    return <>{children}</>;
  }

  // Production mode: require authentication
  // Note: SSO redirect is handled in App.tsx
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Check permissions - redirect to /unauthorized if lacking required permission
  if (user && !hasGovernancePermission(user.role, requiredPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredNavigation) {
    return <NavigationGate page={requiredNavigation}>{children}</NavigationGate>;
  }

  return <>{children}</>;
};
